import React, { Fragment, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useTranslation } from "react-i18next";

import { getDashboardConfig } from "../../config";
import { demoLogout } from "../../utils/session";
import BrandMark from "./BrandMark";

export const MOBILE_NAV_HEIGHT = 56;

/**
 * Navegacion movil (<md): barra superior navy con logo + boton de menu. Al pulsar,
 * se despliega hacia abajo la lista de vistas (agrupadas, con etiqueta) para elegir
 * destino. En escritorio no se renderiza (ahi manda el Sidebar lateral).
 */
function MobileTopNav() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { views: dashboardViews, groups: navGroups } = getDashboardConfig();

    // Cerrar el desplegable al cambiar de vista.
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    let counter = 0;

    return (
        <>
            {/* Barra superior fija */}
            <Box
                component="header"
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: MOBILE_NAV_HEIGHT,
                    bgcolor: "navy.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    zIndex: 1200,
                }}
            >
                <BrandMark size="sm" />
                <IconButton
                    onClick={() => setOpen((o) => !o)}
                    aria-label={open ? "Cerrar menú" : "Abrir menú"}
                    aria-expanded={open}
                    sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(61,79,95,0.3)" } }}
                >
                    {open ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
                </IconButton>
            </Box>

            {/* Velo para cerrar al tocar fuera */}
            <Box
                onClick={() => setOpen(false)}
                sx={{
                    position: "fixed",
                    inset: 0,
                    top: MOBILE_NAV_HEIGHT,
                    bgcolor: "rgba(10,36,62,0.45)",
                    zIndex: 1190,
                    opacity: open ? 1 : 0,
                    pointerEvents: open ? "auto" : "none",
                    transition: "opacity 0.25s ease",
                }}
                aria-hidden
            />

            {/* Desplegable */}
            <Box
                sx={{
                    position: "fixed",
                    top: MOBILE_NAV_HEIGHT,
                    left: 0,
                    right: 0,
                    bgcolor: "navy.main",
                    zIndex: 1195,
                    maxHeight: open ? "78vh" : 0,
                    overflow: "hidden",
                    transition: "max-height 0.28s ease",
                    boxShadow: open ? "0 14px 28px rgba(10,36,62,0.35)" : "none",
                }}
            >
                <Box component="nav" sx={{ py: 1, px: 1.5, overflowY: "auto", maxHeight: "78vh" }}>
                    {navGroups.map((group) => {
                        const items = dashboardViews.filter((v) => v.group === group);
                        if (!items.length) return null;
                        return (
                            <Fragment key={group}>
                                <Typography
                                    sx={{
                                        px: 1.5,
                                        pt: 1.75,
                                        pb: 0.75,
                                        fontSize: 10.5,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.12em",
                                        color: "rgba(255,255,255,0.4)",
                                    }}
                                >
                                    {t(`sidebar.groups.${group}`)}
                                </Typography>
                                {items.map(({ key, path }) => {
                                    counter += 1;
                                    const num = counter;
                                    return (
                                        <Box
                                            key={key}
                                            component={NavLink}
                                            to={path}
                                            end={path === "/"}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.25,
                                                px: 1.5,
                                                py: 1.35,
                                                mb: 0.25,
                                                borderRadius: "10px",
                                                color: "rgba(255,255,255,0.72)",
                                                textDecoration: "none",
                                                transition: "background-color 0.16s ease, color 0.16s ease",
                                                "&:hover": { bgcolor: "rgba(61,79,95,0.25)", color: "#fff" },
                                                "&.active": { bgcolor: "rgba(61,79,95,0.35)", color: "#fff" },
                                            }}
                                        >
                                            <Box component="span" sx={{ fontSize: 11, width: 14, flexShrink: 0, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>
                                                {num}
                                            </Box>
                                            <Typography sx={{ fontSize: 15 }} noWrap>
                                                {t(`nav.${key}`)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Fragment>
                        );
                    })}

                    <Box
                        component="button"
                        type="button"
                        onClick={() => demoLogout(navigate)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            width: "100%",
                            mt: 1,
                            px: 1.5,
                            py: 1.35,
                            border: 0,
                            borderTop: "1px solid rgba(61,79,95,0.4)",
                            borderRadius: 0,
                            bgcolor: "transparent",
                            cursor: "pointer",
                            color: "rgba(255,255,255,0.72)",
                            fontFamily: "inherit",
                            fontSize: 15,
                            textAlign: "left",
                            "&:hover": { color: "#fff" },
                        }}
                    >
                        <LogoutRoundedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                        {t("sidebar.logout")}
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default MobileTopNav;
